import { useRef, useState, type ReactNode, type FormEvent } from "react";
import { router, usePage } from "@inertiajs/react";
import { AuthLayout } from "~/layouts/auth-layout";
import { Input } from "@components/ui/input";
import { Button } from "@components/ui/button";
import { Alert, AlertDescription } from "@components/ui/alert";
import { TurnstileWidget, type TurnstileWidgetHandle } from "@components/auth/TurnstileWidget";
import rocketLaunch from "@assets/rocket-launch.png";

type ForgotPasswordProps = {
  csrf_token: string;
  turnstile_site_key?: string | null;
  errors?: Record<string, string[]>;
};

function ForgotPassword() {
  const { turnstile_site_key, errors: serverErrors } = usePage<ForgotPasswordProps>().props;

  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState<Record<string, string[]>>(serverErrors || {});
  const [processing, setProcessing] = useState(false);
  const turnstileRef = useRef<TurnstileWidgetHandle>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | undefined>();
  const [turnstileReady, setTurnstileReady] = useState(false);

  const turnstileRequired = !!turnstile_site_key;
  const blockedByTurnstile = turnstileRequired && !turnstileReady;

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setProcessing(true);
    setErrors({});

    const token = turnstileRef.current?.getToken() ?? turnstileToken;

    router.post("/users/password", {
      user: { email },
      "cf-turnstile-response": token ?? "",
    }, {
      onError: (errs) => setErrors(errs as unknown as Record<string, string[]>),
      onFinish: () => {
        setProcessing(false);
        turnstileRef.current?.reset();
      },
    });
  }

  const errorMessage = errors?.email?.[0] || errors?.base?.[0];

  return (
    <div className="flex w-full max-w-[508px] flex-col items-center">
      <img src={rocketLaunch} alt="" className="mb-6 h-48 w-48" />

      <h1 className="font-serif text-4xl font-semibold leading-10 text-base-500">
        Reset your password
      </h1>
      <p className="mt-3 max-w-[589px] text-center font-sans text-lg leading-[22px] text-base-500 opacity-70">
        Enter your email and we&apos;ll send you a link to reset your password.
      </p>

      <form onSubmit={handleSubmit} className="mt-8 flex w-full flex-col gap-4">
        {errorMessage && (
          <Alert variant="destructive">
            <AlertDescription>{errorMessage}</AlertDescription>
          </Alert>
        )}

        <Input
          id="email"
          type="email"
          autoComplete="email"
          autoFocus
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="h-12 rounded-lg"
          placeholder="Email"
        />

        {turnstile_site_key && (
          <TurnstileWidget
            ref={turnstileRef}
            siteKey={turnstile_site_key}
            onTokenChange={setTurnstileToken}
            onReady={setTurnstileReady}
          />
        )}

        <Button
          type="submit"
          disabled={processing || blockedByTurnstile}
          className="h-12"
        >
          {processing ? "Sending..." : blockedByTurnstile ? "Verifying…" : "Send reset link"}
        </Button>
      </form>

      <p className="mt-4 font-sans text-base text-base-500">
        Remember your password?{" "}
        <a href="/users/sign_in" className="text-primary-500">
          Sign In
        </a>
      </p>
    </div>
  );
}

ForgotPassword.layout = (page: ReactNode) => <AuthLayout>{page}</AuthLayout>;

export default ForgotPassword;
